import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { commitPreferences } from '@/shared/features/filter-preferences/hooks/use-ui-preferences-writer/use-ui-preferences-writer.utils';
import { useUserUiPreferencesLayer } from '@/shared/features/filter-preferences/hooks/use-user-ui-preferences-layer/use-user-ui-preferences-layer';
import { UiLanguageEnum } from '@/shared/features/language-switcher/language-switcher.types';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { useTemperatureUnit } from '@/shared/hooks/use-temperature-unit/use-temperature-unit';
import {
  TSyncedUiPreferences,
  TUiPreferencesSyncTheme,
} from '@/shared/hooks/use-ui-preferences-sync/use-ui-preferences-sync.types';
import { serializeUiPreferences } from '@/shared/hooks/use-ui-preferences-sync/use-ui-preferences-sync.utils';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useReadUserUiPreferencesQuery } from '@/shared/redux/api/ident/ident-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';
import { TUserUiPreferences } from '@/shared/types/ui-preferences.types';

// Recalls stored UI preferences on login/refresh (server wins) and saves the
// full set back on change. The theme is injected: each experience holds its own.
export const useUiPreferencesSync = ({
  theme,
  setTheme,
}: TUiPreferencesSyncTheme) => {
  const { i18n } = useTranslation();
  const { dateTimeFormat, setDateTimeFormat } = useDateUtils();
  const { temperatureUnit, setTemperatureUnit } = useTemperatureUnit();

  // Not auth.isAuthenticated — that isn't set on refresh.
  const isAuthenticated = !!useAppSelector(selectUser);
  const { data, isSuccess, isFetching } = useReadUserUiPreferencesQuery(
    undefined,
    { skip: !isAuthenticated },
  );
  const userLayer = useUserUiPreferencesLayer();

  const isReady = isAuthenticated && isSuccess && !isFetching;
  const serverPreferences = data?.readUserUiPreferences?.uiPreferences;

  const hydratedRef = useRef(false);
  // True while applying server values, so they aren't saved back.
  const applyingRef = useRef(false);
  const targetRef = useRef<string>('');

  const buildCurrentPreferences = (): TSyncedUiPreferences => ({
    language: i18n.language as UiLanguageEnum,
    theme,
    dateTimeFormat,
    temperatureUnit,
  });

  useEffect(() => {
    if (hydratedRef.current || !isReady) return;
    hydratedRef.current = true;

    const stored = (serverPreferences ?? null) as TUserUiPreferences | null;
    const current = buildCurrentPreferences();

    const target: TSyncedUiPreferences = {
      language: stored?.language ?? current.language,
      theme: stored?.theme ?? current.theme,
      dateTimeFormat: stored?.dateTimeFormat ?? current.dateTimeFormat,
      temperatureUnit: stored?.temperatureUnit ?? current.temperatureUnit,
    };
    targetRef.current = serializeUiPreferences(target);
    applyingRef.current = targetRef.current !== serializeUiPreferences(current);

    if (target.language !== current.language) {
      i18n.changeLanguage(target.language);
    }
    if (target.theme !== current.theme) {
      setTheme(target.theme);
    }
    if (target.dateTimeFormat !== current.dateTimeFormat) {
      setDateTimeFormat(target.dateTimeFormat);
    }
    if (target.temperatureUnit !== current.temperatureUnit) {
      setTemperatureUnit(target.temperatureUnit);
    }
  }, [serverPreferences, isReady]);

  useEffect(() => {
    if (!hydratedRef.current) return;

    const current = buildCurrentPreferences();
    const serialized = serializeUiPreferences(current);

    if (applyingRef.current) {
      // Resume watching once the applied values have settled.
      if (serialized === targetRef.current) {
        applyingRef.current = false;
      }
      return;
    }

    if (serialized === targetRef.current) return;

    const previous = targetRef.current;
    targetRef.current = serialized;

    const save = async () => {
      try {
        // Silent: a background sync has nothing to toast. filterDefaults survive the merge.
        await commitPreferences(userLayer, (blob) => ({ ...blob, ...current }));
      } catch {
        // Roll back so a later change re-fires the failed save, unless a newer
        // value was already queued.
        if (targetRef.current === serialized) {
          targetRef.current = previous;
        }
      }
    };

    save();
  }, [i18n.language, theme, dateTimeFormat, temperatureUnit]);
};
